import React, { useState, useEffect, useRef } from 'react';
import OrderedSelector from './OrderedSelector';
import SubsectionTitle from './SubsectionTitle';
import Spacer from './Spacer';

function MainQuestionnair(props) {
    const {
        isActive,
        onButtonClick,
        data = {},
        isVideoEnded
    } = props;

    const [selectedOption, setSelectedOption] = useState("1");
    const [isOptionSelected, setIsOptionSelected] = useState(false);
    const [isReasonSelected, setIsReasonSelected] = useState(false);
    const [numberOfSelectedOptions, setNumberOfSelectedOptions] = useState(0);

    // 各セクションの回答時間
    const [videoStartTime, setVideoStartTime] = useState(0);

    const [optionStartTime, setOptionStartTime] = useState(null);
    const [optionElapsed, setOptionElapsed] = useState(0);

    const [reasonStartTime, setReasonStartTime] = useState(null);
    const [reasonElapsed, setReasonElapsed] = useState(0);

    // idごとにrefを保存
    const optionRefs = useRef({});

    // ⏩ 最初の選択肢（ラベル）の参照を保持
    const firstOptionRef = useRef(null);
     // 質問がアクティブになった瞬間に「避難方法」タイマー開始
    useEffect(() => {
        if (isActive) {
            setVideoStartTime(Date.now())
        }
    }, [isActive]);

    // 質問がアクティブになった瞬間に「避難方法」タイマー開始
    useEffect(() => {
        // すでに開始済みなら何もしない
        if (isActive && isVideoEnded && !isOptionSelected && optionStartTime === null) {
            setOptionStartTime(Date.now());
        }
    }, [isActive, isVideoEnded, isOptionSelected, optionStartTime]);

    // 避難方法を選んだ瞬間に「避難方法」時間を確定
    const handleOptionClick = (id) => {
        const end = Date.now();
        if (optionStartTime) {
            setOptionElapsed(Math.round((end - optionStartTime)) / 1000);
        }
        setSelectedOption(id);
        setIsOptionSelected(true);
        setIsReasonSelected(false);
        setNumberOfSelectedOptions(0);
        setReasonStartTime(Date.now()); // 理由選択タイマー開始
    };

    // 理由を選んだ瞬間に時間を確定
    const handleReasonSelect = (isSelected) => {
        if (isSelected && reasonStartTime) {
            const end = Date.now();
            setReasonElapsed(Math.round((end - reasonStartTime)) / 1000);
        }
        setIsReasonSelected(isSelected);
    };

    // 戻るボタン処理
    const onBackButtonClick = () => {
        setIsOptionSelected(false);
        setIsReasonSelected(false);
        setNumberOfSelectedOptions(0);
        setReasonStartTime(null);
        setReasonElapsed(0);

        Object.values(optionRefs.current).forEach((input) => {
            if (input) input.checked = false;
        });
    };

    // 🚀 戻った直後に最初の選択肢へフォーカス
    useEffect(() => {
        if (!isOptionSelected && isActive && firstOptionRef.current) {
            firstOptionRef.current.focus();
        }
    }, [isOptionSelected, isActive]);

    return (
        <div
        className={`main-questionnair-item ${isActive ? "is-active" : ""}`}
        inert={!isActive ? "" : undefined}
        >
            {/* ID */}
            <input type="hidden" name={`id${data.id}`} value={data.id} />
            <input
                type="hidden"
                name={`time-video-${data.id}`}
                value={new Date(videoStartTime).toLocaleString()}
            />

            {/* 避難方法の選択 */}
            <div
                className={`main-questionnair-options ${isActive && !isOptionSelected && isVideoEnded ? "is-active" : ""}`}
                inert={isOptionSelected || !isVideoEnded ? "" : undefined}
            >
                <h3 className="main-questionnair-options__title">避難したい方向を選んでください</h3>
                <div className="main-questionnair-options__buttons" data-number-of-selected-options={numberOfSelectedOptions}>
                    {data.options && data.options.map((item, index) => (
                        <label
                            key={item.id}
                            className="main-questionnair-options__button"
                            tabIndex={0}
                            ref={index === 0 ? firstOptionRef : null} // ← 最初の選択肢をrefで保持
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleOptionClick(item.id);
                                }
                            }}
                        >
                        <input
                            ref={(el) => (optionRefs.current[item.id] = el)}
                            type="radio"
                            name={`situation-${data.id}`}
                            value={item.value}
                            onChange={() => handleOptionClick(item.id)}
                            style={{ position: "absolute", opacity: 0 }}
                        />
                            <span
                                className="main-questionnair-options__button-icon"
                                style={{ "--icon": `url(${process.env.PUBLIC_URL}/images/${item.icon})` }}
                            ></span>
                            <span className="main-questionnair-options__button-label">{item.value}</span>
                        </label>
                    ))}
                </div>
                <input type="hidden" name={`time-option-${data.id}`} value={optionElapsed} />
            </div>

            {/* 理由の選択 */}
            <div
                className={`main-questionnair-reasons ${isOptionSelected ? "is-active" : ""}`}
                data-selected-option={selectedOption}
                inert={!isOptionSelected ? "" : undefined}
            >
                <SubsectionTitle label="選択した理由をすべて選択してください。" />
                {data.reasons && data.reasons.map((reasonsGroup) => (
                <React.Fragment key={reasonsGroup.id}>
                    {reasonsGroup.id === selectedOption && (
                    <OrderedSelector
                        options={reasonsGroup.options}
                        fixedOptions={reasonsGroup.fixedOptions}
                        maxChecks={reasonsGroup.options.length + 1}
                        onOptionSelect={handleReasonSelect}
                        setNumberOfSelectedOptions={setNumberOfSelectedOptions}
                        name={`situation-${data.id}`}
                    />
                    )}
                </React.Fragment>
                ))}
                <Spacer sizePC="12" />
                <div className="main-questionnair-item__buttons">
                    <button className="main-questionnair-item__back" type="button" onClick={onBackButtonClick}>
                        避難方法の選択に戻る
                    </button>
                    <button className="main-questionnair-item__submit" type="button" onClick={onButtonClick} disabled={!isReasonSelected}>
                        次へ
                    </button>
                </div>
                <input type="hidden" name={`time-reason-${data.id}`} value={reasonElapsed} />
            </div>
        </div>
    );
}

export default MainQuestionnair;
